// =============================================================================
// PersonReadinessPanel — one person's site readiness, in full
// =============================================================================
// The same component serves three places deliberately:
//
//   * a manager looking at somebody, from the Readiness tab
//   * a manager setting somebody up, under the tick boxes on the People tab
//   * a worker looking at THEMSELVES, from their own portal
//
// One component means a rigger and the director are looking at the same thing.
// If they disagree about what he is missing, that is an argument at a site gate,
// and it is the sort of argument this whole feature exists to prevent.
//
// RLS does the rest: v_employee_requirements is security_invoker, so a field
// worker selecting from it sees their own rows and nobody else's. There is no
// "am I allowed" check in here because there does not need to be.
//
// -----------------------------------------------------------------------------
// THREE THINGS THIS VERSION FIXES, ALL FROM JAMES'S SMOKE TEST
// -----------------------------------------------------------------------------
// 1. ONE LINE PER TICKET, NOT ONE PER ROLE.
//    First Aid appeared twice - once for Field Worker, once for Rigger - because
//    two roles ask for it. That is one ticket. It now appears once, listing the
//    roles that want it. Two roles wanting the same thing is not two problems.
//
// 2. ALTERNATIVES SHOW EVERY OPTION, NOT JUST THE VERDICT.
//    "EWP (>=11m) or EWP (<11m)" collapsed to a single line, so you could not see
//    which one somebody actually held. James: "what if someone only has one, not
//    the other - not a blocker, just a restriction on what they can operate."
//    Exactly right, so the group now shows its overall verdict AND every option
//    underneath with its own status.
//
// 3. IT LINKS TO THE ACTUAL TICKET.
//    A requirement is a dead end without a way to act on it. Every line now goes
//    to that person's licence register, and a missing one goes straight to the
//    add-a-ticket screen.
// =============================================================================

import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, CalendarClock, Layers, Plus, FileText } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Card, Spinner, EmptyState } from "@/components/ui";
import { fmtDate } from "@/lib/dates";
import {
  ReadinessBar, ReadinessLegend, StatusPill, readiness, bestStatus,
  ROLE_KIND_META, STATUS_META, STATUS_ORDER,
  type ReqStatus, type StatusCounts,
} from "./complianceShared";

interface Row {
  role_id: string; role_code: string; role_name: string; role_kind: string;
  alt_group: string; is_mandatory: boolean;
  licence_type_id: string; licence_code: string; licence_name: string;
  licence_id: string | null; expiry_date: string | null; status: ReqStatus;
}

interface Option {
  licence_type_id: string; licence_name: string; licence_code: string;
  licence_id: string | null; expiry_date: string | null; status: ReqStatus;
}

interface Group {
  key: string;
  options: Option[];
  roles: string[];        // every role that asks for this
  kind: string;           // the most senior kind among those roles
  is_mandatory: boolean;
  status: ReqStatus;
  expiry: string | null;
}

export default function PersonReadinessPanel({
  employeeId, compact = false, allowEdit = false,
}: { employeeId: string; compact?: boolean; allowEdit?: boolean }) {

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["readiness_rows", employeeId],
    enabled: !!employeeId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_employee_requirements")
        .select("role_id, role_code, role_name, role_kind, alt_group, is_mandatory, licence_type_id, licence_code, licence_name, licence_id, expiry_date, status")
        .eq("employee_id", employeeId);
      if (error) throw error;
      return (data ?? []) as unknown as Row[];
    },
  });

  // Renewal links live on licence_types and are the single most useful thing to
  // put in front of someone who is missing a ticket - "you need X" is a dead end
  // without "and here is where you get it".
  const { data: renewals = new Map<string, string>() } = useQuery({
    queryKey: ["licence_renewal_urls"],
    queryFn: async () => {
      const { data } = await supabase
        .from("licence_types")
        .select("licence_type_id, renewal_url")
        .not("renewal_url", "is", null);
      return new Map<string, string>(
        (data ?? []).map((r: any) => [r.licence_type_id, r.renewal_url]));
    },
  });

  // Certificate numbers, so a line reads like the ticket in somebody's wallet
  // rather than like a row in a database.
  const { data: numbers = new Map<string, string>() } = useQuery({
    queryKey: ["licence_numbers", employeeId],
    enabled: !!employeeId,
    queryFn: async () => {
      const { data } = await supabase
        .from("employee_licences")
        .select("licence_id, licence_number")
        .eq("employee_id", employeeId)
        .not("licence_number", "is", null);
      return new Map<string, string>(
        (data ?? []).map((r: any) => [r.licence_id, r.licence_number]));
    },
  });

  // ---------------------------------------------------------------------------
  // Collapse to one entry per REQUIREMENT, not per role.
  //
  // The key is the set of licence types that satisfy it. Two roles asking for
  // the same ticket produce the same key and merge into one line; an alternative
  // group produces one key covering all its options. This is what stops First
  // Aid appearing twice because both Field Worker and Rigger want it.
  // ---------------------------------------------------------------------------
  const groups = useMemo<Group[]>(() => {
    // First pass: gather the options of each (role, alt_group) pair.
    const raw = new Map<string, { opts: Map<string, Option>; role: string; kind: string; mand: boolean }>();
    for (const r of rows) {
      const k = `${r.role_id}::${r.alt_group}`;
      const e = raw.get(k) ?? { opts: new Map(), role: r.role_name, kind: r.role_kind, mand: r.is_mandatory };
      e.opts.set(r.licence_type_id, {
        licence_type_id: r.licence_type_id,
        licence_name: r.licence_name,
        licence_code: r.licence_code,
        licence_id: r.licence_id,
        expiry_date: r.expiry_date,
        status: r.status,
      });
      e.mand = e.mand || r.is_mandatory;
      raw.set(k, e);
    }

    // Second pass: merge pairs that resolve to the same set of tickets.
    const merged = new Map<string, Group>();
    for (const e of raw.values()) {
      const opts = Array.from(e.opts.values())
        .sort((a, b) => a.licence_name.localeCompare(b.licence_name));
      const key = opts.map(o => o.licence_type_id).join("|");

      const g = merged.get(key) ?? {
        key, options: opts, roles: [], kind: e.kind,
        is_mandatory: false, status: "missing" as ReqStatus, expiry: null,
      };
      if (!g.roles.includes(e.role)) g.roles.push(e.role);
      g.is_mandatory = g.is_mandatory || e.mand;
      // Most senior kind wins, so a ticket the main role needs is filed under
      // the main role even if a task role also wants it.
      if ((ROLE_KIND_META[e.kind]?.order ?? 9) < (ROLE_KIND_META[g.kind]?.order ?? 9)) g.kind = e.kind;
      merged.set(key, g);
    }

    for (const g of merged.values()) {
      g.status = bestStatus(g.options.map(o => o.status));
      g.expiry = g.options.find(o => o.status === g.status)?.expiry_date ?? null;
      g.roles.sort();
    }
    return Array.from(merged.values());
  }, [rows]);

  const counts = useMemo<StatusCounts>(() => {
    const c: StatusCounts = {};
    for (const g of groups) {
      if (!g.is_mandatory) continue;   // flagged extras never move the number
      c[g.status] = (c[g.status] ?? 0) + 1;
    }
    return c;
  }, [groups]);

  const mandatory = groups.filter(g => g.is_mandatory).length;
  const met = (counts.current ?? 0) + (counts.expiring ?? 0);
  const pct = mandatory ? Math.round((100 * met) / mandatory) : null;
  const verdict = readiness(pct, counts, mandatory);
  const Verdict = verdict.icon;

  const byKind = useMemo(() => {
    const m = new Map<string, Group[]>();
    for (const g of groups) {
      const list = m.get(g.kind) ?? [];
      list.push(g);
      m.set(g.kind, list);
    }
    for (const list of m.values()) {
      // Worst first inside a section - what needs doing is what you want to see.
      list.sort((a, b) =>
        STATUS_ORDER.indexOf(b.status) - STATUS_ORDER.indexOf(a.status)
        || a.options[0].licence_name.localeCompare(b.options[0].licence_name));
    }
    return Array.from(m.entries())
      .sort((a, b) => (ROLE_KIND_META[a[0]]?.order ?? 9) - (ROLE_KIND_META[b[0]]?.order ?? 9));
  }, [groups]);

  if (isLoading) {
    return <div className="py-10 flex justify-center"><Spinner size={18} /></div>;
  }

  if (!groups.length) {
    return (
      <Card>
        <EmptyState
          icon={Layers}
          title="Nothing required yet"
          description="Set a main role and tick what this person is signed off for, and their requirements fill in here automatically from the tickets already on file."
        />
      </Card>
    );
  }

  // ---------------------------------------------------------------------------
  // One option line. Used both for a stand-alone requirement and for each member
  // of an alternative group.
  // ---------------------------------------------------------------------------
  function OptionLine({ o, dim }: { o: Option; dim?: boolean }) {
    const url = renewals.get(o.licence_type_id);
    const number = o.licence_id ? numbers.get(o.licence_id) : undefined;
    return (
      <div className="flex items-start gap-3 py-1">
        <div className="min-w-0 flex-1">
          <div className={`text-[13px] ${dim && o.status === "missing" ? "text-text-muted" : "text-text"}`}>
            {o.licence_name}
          </div>
          <div className="text-[11px] text-text-muted mt-0.5 flex items-center gap-2 flex-wrap">
            {number && <span className="font-mono">{number}</span>}
            {o.expiry_date && (
              <span className="inline-flex items-center gap-1">
                <CalendarClock size={11} />
                {o.status === "expired" ? "expired" : "expires"} {fmtDate(o.expiry_date)}
              </span>
            )}
            {!o.licence_id && <span className="text-text-dim">nothing on file</span>}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {url && o.status !== "current" && (
            <a href={url} target="_blank" rel="noopener noreferrer"
               className="text-[11px] text-accent hover:text-accent-bright underline inline-flex items-center gap-1">
              Renew <ExternalLink size={10} />
            </a>
          )}
          {allowEdit && (
            o.licence_id
              ? <Link to={`/hr/licences/${employeeId}`}
                      className="text-[11px] text-text-muted hover:text-accent inline-flex items-center gap-1"
                      title="Open this person's licence register">
                  <FileText size={11} /> Ticket
                </Link>
              : <Link to={`/hr/licences/${employeeId}/new`}
                      className="text-[11px] text-accent hover:text-accent-bright inline-flex items-center gap-1"
                      title="Add this ticket to their record">
                  <Plus size={11} /> Add
                </Link>
          )}
          <StatusPill status={o.status} small />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ---- The headline. Number, word and icon together. ------------------ */}
      <Card>
        <div className="p-5">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-baseline gap-3">
                <span className="text-[40px] leading-none font-semibold tracking-tight text-text tabular-nums">
                  {pct == null ? "—" : `${pct}%`}
                </span>
                <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded ${verdict.chip} ${verdict.text} text-[12px] font-medium`}>
                  <Verdict size={13} /> {verdict.label}
                </span>
              </div>
              <div className="text-[12px] text-text-muted mt-1.5">
                {met} of {mandatory} requirements met
                {(counts.expiring ?? 0) > 0 && <> · {counts.expiring} expiring within 60 days</>}
              </div>
            </div>
            {allowEdit && (
              <Link to={`/hr/licences/${employeeId}`}
                    className="text-[12px] text-accent hover:underline inline-flex items-center gap-1.5">
                <FileText size={13} /> All tickets on file
              </Link>
            )}
          </div>

          <div className="mt-4">
            <ReadinessBar counts={counts} height={10} />
          </div>
          <div className="mt-2.5">
            <ReadinessLegend counts={counts} />
          </div>

          {/* Says what the number covers. Without this, someone with no roles
              set reads 100% and thinks they are ready for anything. */}
          <p className="text-[11px] text-text-dim mt-3">
            Measured against {mandatory} mandatory requirement{mandatory === 1 ? "" : "s"} from
            the role{groups.length === 1 ? "" : "s"} set for this person — not everything Coretel does.
          </p>
        </div>
      </Card>

      {/* ---- The detail ----------------------------------------------------- */}
      {byKind.map(([kind, list]) => (
        <Card key={kind}>
          <div className="p-5">
            <div className="flex items-baseline gap-2 mb-3">
              <h3 className="text-[13px] font-semibold text-text">
                {ROLE_KIND_META[kind]?.label ?? kind}
              </h3>
              <span className="text-[11px] text-text-muted">
                {ROLE_KIND_META[kind]?.blurb}
              </span>
            </div>

            {/* 31 Aug 2026 - James: "the required tick box... my profile still
                has me needing this as it's missing but it's not required".
                Untick did work for the number; the row just kept its Missing
                pill. Recommended-and-not-held now drops to a quiet footer
                line instead of sitting in the list looking like a gap. A
                recommended ticket that IS held still shows normally. */}
            {(() => {
              const main = list.filter(g => g.is_mandatory || g.status !== "missing");
              const optional = list.filter(g => !g.is_mandatory && g.status === "missing");
              return (
            <>
            {main.length > 0 && (
            <div className="divide-y divide-line border border-line rounded-md overflow-hidden">
              {main.map(g => {
                const needsAction = g.status !== "current";
                const isAlt = g.options.length > 1;
                return (
                  <div key={g.key}
                       className={`px-3 py-2.5 ${needsAction ? "bg-ink-raised" : ""}`}>
                    {isAlt ? (
                      <>
                        {/* The verdict for the group, then every option under it.
                            Seeing that somebody holds the under-11m EWP ticket but
                            not the high-risk one is the whole point - it is not a
                            failure, it is a limit on what they can operate. */}
                        <div className="flex items-center justify-between gap-3 mb-1">
                          <span className="text-[10px] uppercase tracking-wider text-text-muted">
                            Any one of these
                          </span>
                          <StatusPill status={g.status} small />
                        </div>
                        <div className="divide-y divide-line/60 pl-2 border-l-2 border-line">
                          {g.options.map(o => (
                            <OptionLine key={o.licence_type_id} o={o} dim />
                          ))}
                        </div>
                      </>
                    ) : (
                      <OptionLine o={g.options[0]} />
                    )}

                    <div className="text-[11px] text-text-dim mt-1 flex items-center gap-2 flex-wrap">
                      <span>Needed for {g.roles.join(" · ")}</span>
                      {!g.is_mandatory && (
                        <span className="px-1.5 py-0.5 rounded bg-text/8 text-text-muted">
                          recommended, not required
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            )}
            {optional.length > 0 && (
              <div className={`text-[11px] text-text-dim ${main.length ? "mt-3" : ""}`}>
                <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">
                  Recommended for their roles, not required — not held
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1">
                  {optional.map(g => (
                    <span key={g.key} className="inline-flex items-center gap-1.5">
                      <span className="text-text-muted">{g.options.map(o => o.licence_name).join(" or ")}</span>
                      <span>· {g.roles.join(" · ")}</span>
                      {allowEdit && (
                        <Link to={`/hr/licences/${employeeId}/new`}
                              className="text-accent hover:text-accent-bright inline-flex items-center gap-0.5"
                              title="Add this ticket to their record">
                          <Plus size={10} /> Add
                        </Link>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}
            </>
              );
            })()}
          </div>
        </Card>
      ))}

      {/* Plain-English key. The bar is a glance; this is what it means. */}
      <div className="text-[11px] text-text-dim leading-relaxed">
        {STATUS_ORDER.map(s => (
          <span key={s} className="mr-4 whitespace-nowrap">
            <span className={`inline-block w-2 h-2 rounded-sm mr-1.5 align-middle ${STATUS_META[s].fill}`} />
            <span className="text-text-muted">{STATUS_META[s].label}</span> — {STATUS_META[s].blurb}
          </span>
        ))}
      </div>

      {compact && <div className="h-1" />}
    </div>
  );
}
